import type { CareProfile } from './profile';
import type { Creature, CreatureState } from '@/domain/creature/types';

export type CareTier = 'ruim' | 'medio' | 'bom' | 'secreto';

export interface CareScoreBreakdown {
  score: number;
  diet: number;
  discipline: number;
  hygiene: number;
  health: number;
  attention: number;
}

const clamp = (v: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, v));

/** Delta de contadores desde `snapshot` (ex.: entrada no teen). `null` = usa o profile inteiro como delta. */
export function diffCareProfile(current: CareProfile, snapshot: CareProfile | null): CareProfile {
  if (snapshot === null) return current;
  const keys: Array<Exclude<keyof CareProfile, 'lastToyId' | 'consecutiveToyUses'>> = [
    'healthyMeals', 'snacks', 'plays', 'cleanups', 'lateCleanups', 'heals', 'sickTimeMs',
    'correctDisciplines', 'wrongDisciplines', 'tantrumsResolved', 'refusals', 'heartyMeals',
    'activePlays', 'comfortPlays', 'focusPlays',
  ];
  const result = { ...current };
  for (const k of keys) {
    result[k] = Math.max(0, current[k] - snapshot[k]);
  }
  result.lastToyId = current.lastToyId;
  result.consecutiveToyUses = current.consecutiveToyUses;
  return result;
}

/**
 * Placar 0-100, centrado em 50 (neutro). Pesos são ponto de partida (spec:
 * "Fora de escopo" — balanceamento fino fica pra depois de jogar).
 */
export function computeCareScore(delta: CareProfile): CareScoreBreakdown {
  const diet = clamp((delta.healthyMeals + delta.heartyMeals - delta.snacks) * 4, -20, 20);
  const discipline = clamp((delta.correctDisciplines - delta.wrongDisciplines) * 5, -20, 20);
  const hygiene = clamp(-delta.lateCleanups * 4, -20, 0);
  const health = clamp(delta.heals * 3 - delta.sickTimeMs / 60_000, -20, 10);
  const attention = clamp(-delta.refusals * 2, -20, 0);
  const score = clamp(50 + diet + discipline + hygiene + health + attention, 0, 100);
  return { score, diet, discipline, hygiene, health, attention };
}

/**
 * Overrides específicos (eco do Tamagotchi original) rodam antes das faixas
 * de placar e podem forçar um ramo independente do placar.
 */
export function resolveCareTier(scoreFinal: number, state: CreatureState, delta: CareProfile): CareTier {
  if (state.weight >= 80) return 'ruim';
  if (delta.refusals >= 8 || delta.wrongDisciplines >= delta.correctDisciplines + 4) return 'ruim';
  if (scoreFinal >= 90 && delta.refusals >= 1 && delta.refusals <= 3) return 'secreto';
  if (scoreFinal >= 71) return 'bom';
  if (scoreFinal >= 41) return 'medio';
  return 'ruim';
}

/** Amplitude de deformação visual: -1 (ruim) .. 0 (médio/ainda não revelado) .. 1 (bom/secreto). */
export function resolveDeformAmplitude(tier: CareTier | null): number {
  if (tier === 'bom' || tier === 'secreto') return 1;
  if (tier === 'ruim') return -1;
  return 0;
}

/** Peso da manifestação visual da predisposição antes da revelação (Fase 3 da spec). */
const PREDISPOSITION_PREVIEW_WEIGHT = 0.2;

/**
 * Amplitude de deformação a aplicar no render, considerando o ciclo de vida
 * inteiro: antes da revelação (careTier null) mostra uma prévia sutil da
 * predisposição herdada; depois da revelação, usa o resultado definitivo.
 */
export function resolveDeformAmplitudeForCreature(c: Creature): number {
  if (c.lifecycle.careTier !== null) return resolveDeformAmplitude(c.lifecycle.careTier);
  return ((c.genome.bodyPredisposition - 50) / 50) * PREDISPOSITION_PREVIEW_WEIGHT;
}
